/**
 * Refine authProvider — связывает Refine login/logout/check с нашим
 * /admin/auth/telegram + /admin/me + /admin/auth/logout.
 *
 * TG Login Widget на /login странице сам шлёт нам payload как user-callback.
 * authProvider.login() получает этот payload, шлёт на backend и cookie
 * выставляется автоматически (HttpOnly, домен .bemli.org).
 *
 * После этого все запросы из dataProvider идут с credentials:"include"
 * → cookie ездит вместе с ними.
 */
import type { AuthProvider } from "@refinedev/core";
import { API_URL } from "./dataProvider";

export interface TgLoginPayload {
  id: number;
  first_name: string;
  last_name?: string;
  username?: string;
  photo_url?: string;
  auth_date: number;
  hash: string;
}

export const authProvider: AuthProvider = {
  // Refine вызывает login() из нашей кастомной страницы /login,
  // передавая payload, полученный от TG Login Widget.
  async login(params: TgLoginPayload | { tgPayload: TgLoginPayload }) {
    const payload =
      "tgPayload" in params ? params.tgPayload : (params as TgLoginPayload);
    if (!payload || !payload.hash) {
      return {
        success: false,
        error: { name: "TgLoginError", message: "Не получены данные от Telegram" },
      };
    }
    const r = await fetch(`${API_URL}/admin/auth/telegram`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (r.status === 200) return { success: true, redirectTo: "/" };
    let detail = `HTTP ${r.status}`;
    try {
      const j = await r.json();
      detail = j.detail || detail;
    } catch { /* ignore */ }
    return {
      success: false,
      error: { name: "TgLoginError", message: detail },
    };
  },

  async logout() {
    await fetch(`${API_URL}/admin/auth/logout`, {
      method: "POST",
      credentials: "include",
    }).catch(() => null);
    return { success: true, redirectTo: "/login" };
  },

  async check() {
    const r = await fetch(`${API_URL}/admin/me`, {
      credentials: "include",
    });
    if (r.status === 200) {
      const me = await r.json();
      return { authenticated: true, redirectTo: undefined, ...me };
    }
    return {
      authenticated: false,
      redirectTo: "/login",
      logout: true,
    };
  },

  async getIdentity() {
    const r = await fetch(`${API_URL}/admin/me`, { credentials: "include" });
    if (r.status !== 200) return null;
    const me = await r.json();
    return {
      id: me.tg_id,
      name: me.first_name || me.username || `tg:${me.tg_id}`,
      role: me.role,
      avatar: me.photo_url,
    };
  },

  async onError(error) {
    if (error?.statusCode === 401 || error?.statusCode === 403) {
      return { logout: true, redirectTo: "/login" };
    }
    return {};
  },
};
